import { getEffectiveMembership } from "./membership.js";
import { getFreeTrialProduct, getPricingProducts } from "../webapp/js/pricing-config.js";

/**
 * Cloud hosting entitlement helpers.
 *
 * These functions are the sole source of truth for cloud quota and
 * eligibility decisions on the Pages control plane.
 *
 * Quotas live on the product in webapp/js/pricing-config.js.
 * getCloudLimitsForProduct reads them. Unknown ids, including public, use the free trial.
 *
 * Convention: `null` always means "unlimited" throughout the system.
 *
 * The subscription that counts for an existing asset is the **owner's**,
 * never the accessing collaborator's.
 */

import {
  MAX_SHARD_BYTES as PROTOCOL_MAX_SHARD_BYTES,
  MAX_YJS_PACKET_BYTES as PROTOCOL_MAX_YJS_PACKET_BYTES,
} from "./generated/collab-protocol-limits.js";

export const MAX_SHARD_BYTES = PROTOCOL_MAX_SHARD_BYTES;
export const MAX_YJS_PACKET_BYTES = PROTOCOL_MAX_YJS_PACKET_BYTES;
export const WARNING_SHARD_BYTES = Math.floor(MAX_SHARD_BYTES * 0.75);

export function cloudLimitsFromProduct(product) {
  return {
    maxFontsOwned: product?.maxFontsOwned ?? null,
    maxGlyphsPerFont: product?.maxGlyphsPerFont ?? null,
    maxInvitesPerAsset: product?.maxInvitesPerAsset ?? null,
  };
}

export const LOCAL_CLOUD_DEV_LIMITS = {
  maxFontsOwned: null,
  maxGlyphsPerFont: null,
  maxInvitesPerAsset: null,
};

function resolveRequestHostname(options = {}) {
  if (typeof options.hostname === "string" && options.hostname.trim()) {
    return options.hostname.trim().toLowerCase();
  }
  const request = options.request;
  if (request?.url) {
    try {
      return new URL(request.url).hostname.toLowerCase();
    } catch {
      return "";
    }
  }
  return "";
}

export function isLocalDevHostname(hostname) {
  const host = String(hostname || "")
    .trim()
    .toLowerCase();
  return (
    host === "localhost" ||
    host === "127.0.0.1" ||
    host === "::1" ||
    host === "[::1]"
  );
}

/**
 * LOCAL_CLOUD_DEV_ENABLED only lifts quotas on loopback hostnames.
 * Callers must pass `request` or `hostname`; otherwise the flag is ignored.
 */
export function isLocalCloudDevEnabled(options = {}) {
  if (options.env?.LOCAL_CLOUD_DEV_ENABLED !== "true") {
    return false;
  }
  return isLocalDevHostname(resolveRequestHostname(options));
}

/**
 * @param {string|null|undefined} productId
 * @returns {{ maxFontsOwned: number|null, maxGlyphsPerFont: number|null, maxInvitesPerAsset: number|null }}
 */
export function getCloudLimitsForProduct(productId) {
  const products = getPricingProducts();
  const product = products[productId] || getFreeTrialProduct() || products.basic;
  return cloudLimitsFromProduct(product);
}

async function readLocalCloudLimitsFixture(db, userId) {
  if (!db?.prepare || !userId) {
    return null;
  }
  try {
    const row = await db
      .prepare("SELECT tier FROM cloud_limits_fixtures WHERE user_id = ?")
      .bind(userId)
      .first();
    if (row?.tier === "basic" || row?.tier === "unlimited") {
      return row.tier;
    }
  } catch {
    return null;
  }
  return null;
}

async function getCloudLimitsForUser(db, userId, options = {}) {
  const tier = await readLocalCloudLimitsFixture(db, userId);
  if (tier === "basic") {
    return getCloudLimitsForProduct("basic");
  }
  // Staging e2e writes this fixture. Local wrangler still lifts quotas when
  // the fixture row is absent.
  if (tier === "unlimited" || isLocalCloudDevEnabled(options)) {
    return { ...LOCAL_CLOUD_DEV_LIMITS };
  }
  if (!db?.prepare || !userId) {
    return getCloudLimitsForProduct("public");
  }
  const membershipState = await getEffectiveMembership(db, userId, options);
  const productId = membershipState.effective?.productId || "public";
  return getCloudLimitsForProduct(productId);
}

/**
 * Returns true if the user currently has an active cloud hosting grant.
 *
 * Current policy: all authenticated users may host cloud fonts.
 *
 * @param {import('@cloudflare/workers-types').D1Database} db
 * @param {string} userId
 * @returns {Promise<boolean>}
 */
export async function isCloudHostingEnabled(_db, userId) {
  return Boolean(userId);
}

/**
 * Returns the maximum number of font assets this user may own, or null for
 * unlimited.
 *
 * @param {import('@cloudflare/workers-types').D1Database} db
 * @param {string} userId
 * @returns {Promise<number|null>}
 */
export async function getMaxFontsOwned(db, userId, options = {}) {
  const limits = await getCloudLimitsForUser(db, userId, options);
  return limits.maxFontsOwned;
}

/**
 * Returns the maximum number of glyphs this user may store in one owned font,
 * or null for unlimited.
 *
 * @param {import('@cloudflare/workers-types').D1Database} db
 * @param {string} userId
 * @returns {Promise<number|null>}
 */
export async function getMaxGlyphsPerFont(db, userId, options = {}) {
  const limits = await getCloudLimitsForUser(db, userId, options);
  return limits.maxGlyphsPerFont;
}

/**
 * Returns the maximum number of additional users that may be invited per
 * font asset, or null for unlimited. Pass the **owner** user id.
 *
 * @param {import('@cloudflare/workers-types').D1Database} db
 * @param {string} userId
 * @returns {Promise<number|null>}
 */
export async function getMaxInvitesPerAsset(db, userId, options = {}) {
  const limits = await getCloudLimitsForUser(db, userId, options);
  return limits.maxInvitesPerAsset;
}

/**
 * Returns the maximum number of assistant operations this user may perform,
 * or null for unlimited.
 *
 * Current policy: unlimited assistant usage for all authenticated users.
 * Future: will return the tier-specific cap.
 *
 * @param {import('@cloudflare/workers-types').D1Database} _db
 * @param {string} _userId
 * @returns {Promise<number|null>}
 */
export async function getMaxAssistantUsage(_db, _userId) {
  return null;
}

function normalizeSnapshotRetentionDays(value) {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const parsed = Number.parseInt(String(value), 10);
  if (!Number.isFinite(parsed) || parsed < 1) {
    return null;
  }

  return parsed;
}

function parseSnapshotRetentionPolicy(rawPolicy) {
  if (!rawPolicy) {
    return null;
  }

  let policy = rawPolicy;
  if (typeof rawPolicy === "string") {
    try {
      policy = JSON.parse(rawPolicy);
    } catch {
      return null;
    }
  }

  if (!policy || typeof policy !== "object") {
    return null;
  }

  const sourceProductDays =
    policy.productDays && typeof policy.productDays === "object"
      ? policy.productDays
      : policy;
  const productDays = Object.fromEntries(
    Object.entries(sourceProductDays)
      .filter(([key]) => key !== "defaultDays" && key !== "productDays")
      .map(([key, value]) => [key, normalizeSnapshotRetentionDays(value)]),
  );

  return {
    defaultDays: normalizeSnapshotRetentionDays(policy.defaultDays),
    productDays,
  };
}

function resolveSnapshotRetentionDays(productId, policy) {
  if (!policy) {
    return null;
  }

  if (Object.hasOwn(policy.productDays, productId)) {
    return policy.productDays[productId];
  }

  return policy.defaultDays;
}

/**
 * Returns the minimum number of days named snapshots are retained for this
 * user, or null for unlimited.
 *
 * Phase 1: always returns null (unlimited).
 * Future: will return the tier-appropriate retention window in days.
 *
 * @param {import('@cloudflare/workers-types').D1Database} db
 * @param {string} userId
 * @param {{ env?: Record<string, string>, now?: number, retentionPolicy?: { defaultDays?: number|null, productDays?: Record<string, number|null> } }} [options]
 * @returns {Promise<number|null>}
 */
export async function getSnapshotRetentionDays(db, userId, options = {}) {
  const policy =
    options.retentionPolicy ||
    parseSnapshotRetentionPolicy(
      options.env?.CLOUD_SNAPSHOT_RETENTION_POLICY_JSON,
    );
  if (!policy) {
    return null;
  }

  const membershipState = await getEffectiveMembership(db, userId, options);
  const productId = membershipState.effective?.productId || "public";
  return resolveSnapshotRetentionDays(productId, policy);
}
